// Reports — three streamed-download cards (Statement / Audit /
// Reconciliation), each backed by GET /v1/merchants/:id/reports/<name>.
//
// Each card carries its own from/to inputs and format toggle so an
// operator can compare e.g. "monthly statement" + "weekly recon" side
// by side without losing context. Downloads stream as a Blob and land
// via the saveBlob helper in src/utils/csv.ts.
//
// Tenant scope: the merchant_id comes from useUI's currentMerchantId
// (localStorage-mirrored, same source the dashboard uses). When
// currentMerchantId is null (admin who hasn't picked a merchant yet),
// we surface a friendly empty-state instead of letting the API call
// fire a 401.
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Segmented } from "@/components/UI";
import { I } from "@/components/Icons";
import {
  downloadReport,
  type ReportFormat,
  type ReportName,
} from "@/api/reports";
import { apiErrorMessage } from "@/utils/apiError";
import { saveBlob } from "@/utils/csv";
import { useUI, type ToastEntry } from "@/store/ui";
import { useAuth, effectiveRole, isAdminRole } from "@/store/auth";
import { ReconciliationCard } from "@/components/ReconciliationCard";
import { ymdLocal } from "@/utils/dateRange";

type AddToast = (toast: Omit<ToastEntry, "id">) => void;

const REPORTS: Array<{
  key: ReportName;
  title: string;
  blurb: string;
}> = [
  {
    key: "statement",
    title: "Wallet statement",
    blurb:
      "One line per movement, oldest first, in rupees: amount, the service fee charged on it, and the wallet balance before and after. Balances carry in from before the window, so the opening figure is real.",
  },
  {
    key: "audit",
    title: "Audit report",
    blurb:
      "Chronological audit_log entries scoped to this merchant. Captures who did what, from where, with the before/after JSON diff.",
  },
  {
    key: "reconciliation",
    title: "Reconciliation report",
    blurb:
      "One row per payout — internal status, latest provider status, UTR, beneficiary details, and a state_consistent flag for the operator's first attention target.",
  },
];

const FORMAT_OPTIONS: Array<{ value: ReportFormat; label: string }> = [
  { value: "csv", label: "CSV" },
  { value: "json", label: "JSON" },
  { value: "xlsx", label: "Excel" },
];

// defaultRange returns [first-of-this-month, today] as YYYY-MM-DD —
// what most operators want as their starting window (current
// month-to-date). Stays under the 90-day cap by construction.
function defaultRange(): { from: string; to: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(first), to: fmt(now) };
}

export function Reports() {
  const currentMerchantId = useUI((s) => s.currentMerchantId);
  const addToast = useUI((s) => s.addToast);
  const role = useAuth((s) => effectiveRole(s));

  // Balance reconciliation is platform-wide (the rail account is shared
  // by every merchant), so it renders for admins whether or not a
  // merchant is picked. Its period lives in the URL like the dashboard's.
  const [searchParams, setSearchParams] = useSearchParams();
  const today = useMemo(() => ymdLocal(new Date()), []);
  const reconFrom = searchParams.get("from") ?? today;
  const reconTo = searchParams.get("to") ?? today;
  const setReconRange = ({ from, to }: { from: string; to: string }) => {
    const next = new URLSearchParams(searchParams);
    next.set("from", from);
    next.set("to", to);
    setSearchParams(next, { replace: true });
  };
  const recon = isAdminRole(role) ? (
    <ReconciliationCard from={reconFrom} to={reconTo} onRange={setReconRange} />
  ) : null;

  if (currentMerchantId == null) {
    return (
      <div className="page" style={{ padding: 24 }}>
        <div className="card" style={{ padding: 16, maxWidth: 560 }}>
          <div className="eyebrow">Reports</div>
          <p className="dim" style={{ fontSize: 13, marginTop: 8 }}>
            Pick a merchant from the topbar to access this merchant's statement,
            audit, and reconciliation reports.
          </p>
        </div>
        {recon}
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: 24 }}>
      <header style={{ marginBottom: 16 }}>
        <div className="eyebrow">Reports</div>
        <h1
          style={{
            fontFamily: "var(--font-serif, 'Instrument Serif', serif)",
            fontSize: 28,
            margin: "4px 0",
          }}
        >
          Downloads
        </h1>
        <p className="dim" style={{ fontSize: 13, maxWidth: 720 }}>
          Stream a CSV, JSON, or Excel file for any 90-day window. Reports are
          also pullable from the API with an <code>stk_…</code> key carrying the{" "}
          <code>payouts:read</code> scope — same endpoint, same parameters.
        </p>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
          gap: 16,
        }}
      >
        {REPORTS.map((r) => (
          <ReportCard
            key={r.key}
            report={r.key}
            title={r.title}
            blurb={r.blurb}
            merchantId={currentMerchantId}
            onToast={addToast}
          />
        ))}
      </div>

      {recon}
    </div>
  );
}

interface ReportCardProps {
  report: ReportName;
  title: string;
  blurb: string;
  merchantId: number;
  onToast: AddToast;
}

function ReportCard({
  report,
  title,
  blurb,
  merchantId,
  onToast,
}: ReportCardProps) {
  const init = defaultRange();
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [format, setFormat] = useState<ReportFormat>("csv");
  const [busy, setBusy] = useState(false);

  async function handleDownload() {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await downloadReport({
        merchantId,
        report,
        from,
        to,
        format,
      });
      // Filename is best-effort — the server already set Content-Disposition,
      // but a.download takes precedence in modern browsers. Use a
      // human-readable stem; server-side stem is also fine but we don't
      // parse Content-Disposition here to keep this thin.
      const stem = `${report}-${merchantId}-${from}-${to}`;
      const ext =
        format === "json" ? "json" : format === "xlsx" ? "xlsx" : "csv";
      saveBlob(`${stem}.${ext}`, blob);
      onToast({
        variant: "success",
        title: "Report ready",
        body: `${title} (${format.toUpperCase()})`,
      });
    } catch (err) {
      // Backend 4xx errors come back as a JSON Blob; pull the body out
      // and map via apiErrorMessage to surface the structured code
      // (window_too_large, unsupported_format, ...).
      const message = await blobErrorMessage(err);
      onToast({
        variant: "error",
        title: "Could not download",
        body: message,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="card"
      style={{ padding: 16, display: "flex", flexDirection: "column" }}
    >
      <div
        className="row gap-8"
        style={{ alignItems: "baseline", marginBottom: 6 }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h3>
      </div>
      <p className="dim" style={{ fontSize: 12, marginBottom: 12 }}>
        {blurb}
      </p>

      <div className="row gap-8" style={{ flexWrap: "wrap", marginBottom: 10 }}>
        <div
          className="field"
          style={{ marginBottom: 0, flex: 1, minWidth: 130 }}
        >
          <label className="label">From</label>
          <input
            className="input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div
          className="field"
          style={{ marginBottom: 0, flex: 1, minWidth: 130 }}
        >
          <label className="label">To</label>
          <input
            className="input"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
      </div>

      <div
        className="row gap-8"
        style={{
          alignItems: "center",
          marginTop: "auto",
          justifyContent: "space-between",
        }}
      >
        <Segmented
          value={format}
          onChange={setFormat}
          options={FORMAT_OPTIONS}
          label="Format"
        />
        <button
          type="button"
          className="btn accent sm"
          onClick={handleDownload}
          disabled={busy || !from || !to}
        >
          {busy ? (
            "Preparing…"
          ) : (
            <>
              <I.Download size={11} /> Download
            </>
          )}
        </button>
      </div>
    </section>
  );
}

// blobErrorMessage extracts a useful message from an axios error whose
// response body is a Blob (because responseType:'blob' applies to the
// success AND error paths). Tries to read the blob as JSON and pull the
// structured {error, code, details}; falls back to a generic message.
async function blobErrorMessage(err: unknown): Promise<string> {
  type AxiosErrShape = { response?: { data?: Blob | { error?: string } } };
  const ax = err as AxiosErrShape;
  const data = ax.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text) as {
        error?: string;
        code?: string;
        details?: string;
      };
      if (parsed.code === "window_too_large")
        return "Window too large — pick a range ≤ 90 days.";
      if (parsed.code === "window_reversed")
        return '"To" must be later than "From".';
      if (parsed.code === "window_required")
        return "Pick a from/to range first.";
      if (parsed.code === "unsupported_format") return "Format not supported.";
      if (parsed.details) return parsed.details;
      if (parsed.error) return parsed.error;
    } catch {
      // fall through
    }
  }
  return apiErrorMessage(err, "Download failed.");
}
